/**
 * ResetPasswordPage — consume a password-reset token and set a new password
 *
 * URL: /auth/reset-password/:token
 *
 * Four states:
 *  1. Validating  — spinner while calling GET /auth/reset-password/{token}
 *  2. Invalid     — token expired, used, or not found → error screen
 *  3. Form        — new-password + confirm fields
 *  4. Success     — confirmation with auto-redirect to /login after 3 seconds
 *
 * Visual shell mirrors LoginPage / ForgotPasswordPage for a coherent auth
 * experience (split-screen, brand-glow, surface-raised-2 right panel).
 */

import { useState, useEffect, useRef, type FormEvent } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { LockClosedIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import PasswordToggleButton from '@/components/ui/PasswordToggleButton';
import { ExclamationTriangleIcon } from '@heroicons/react/24/solid';
import { validatePasswordResetToken, resetPassword } from '@/services/authApi';
import type { PasswordResetTokenPreview } from '@/services/authApi';
import Button from '@/components/Button';
import { getErrorMessage } from '@/utils/errors';

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function InvalidTokenScreen({
  errorCode,
  message,
}: {
  errorCode: string | undefined;
  message: string | undefined;
}) {
  const title =
    errorCode === 'expired'
      ? 'Link expired'
      : errorCode === 'used'
        ? 'Link already used'
        : 'Invalid link';

  const body =
    message ||
    (errorCode === 'expired'
      ? 'This password reset link has expired. Please request a new one.'
      : errorCode === 'used'
        ? 'This link has already been used. If you need to reset your password again, request a new link.'
        : 'This password reset link is invalid or does not exist.');

  return (
    <div style={{ textAlign: 'center' }}>
      <div
        style={{
          width: '64px',
          height: '64px',
          borderRadius: '50%',
          backgroundColor: 'rgba(248, 113, 113, 0.12)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 24px',
        }}
      >
        <ExclamationTriangleIcon
          style={{ width: '28px', height: '28px', color: 'var(--color-signal-bad)' }}
        />
      </div>
      <h2
        className="text-display-lg"
        style={{
          color: 'var(--color-text-primary)',
          margin: '0 0 12px',
          letterSpacing: '-0.5px',
        }}
      >
        {title}
      </h2>
      <p
        className="text-body-md"
        style={{
          color: 'var(--color-text-secondary)',
          margin: '0 0 32px',
          lineHeight: 1.6,
        }}
      >
        {body}
      </p>
      <Link
        to="/auth/forgot-password"
        style={{
          display: 'inline-block',
          padding: '10px 24px',
          backgroundColor: 'var(--color-brand-primary)',
          color: 'var(--color-text-inverse)',
          borderRadius: '8px',
          textDecoration: 'none',
          fontWeight: 600,
          fontSize: '14px',
          marginBottom: '16px',
        }}
      >
        Request new reset link
      </Link>
      <div style={{ marginTop: '12px' }}>
        <Link
          to="/login"
          style={{
            color: 'var(--color-text-muted)',
            textDecoration: 'none',
            fontSize: '13px',
          }}
        >
          Back to sign in
        </Link>
      </div>
    </div>
  );
}

function SuccessScreen() {
  const navigate = useNavigate();
  const [countdown, setCountdown] = useState(3);

  useEffect(() => {
    const timer = setInterval(() => {
      setCountdown((c) => {
        if (c <= 1) {
          clearInterval(timer);
          navigate('/login');
          return 0;
        }
        return c - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [navigate]);

  return (
    <div style={{ textAlign: 'center' }}>
      <div
        style={{
          width: '64px',
          height: '64px',
          borderRadius: '50%',
          backgroundColor: 'rgba(34, 197, 94, 0.12)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 24px',
        }}
      >
        <CheckCircleIcon
          style={{ width: '28px', height: '28px', color: '#22c55e' }}
        />
      </div>
      <h2
        className="text-display-lg"
        style={{
          color: 'var(--color-text-primary)',
          margin: '0 0 12px',
          letterSpacing: '-0.5px',
        }}
      >
        Password reset!
      </h2>
      <p
        className="text-body-md"
        style={{
          color: 'var(--color-text-secondary)',
          margin: '0 0 32px',
          lineHeight: 1.6,
        }}
      >
        Your password has been updated. Redirecting you to sign in in {countdown}
        {countdown === 1 ? ' second' : ' seconds'}…
      </p>
      <Link
        to="/login"
        style={{
          display: 'inline-block',
          padding: '10px 24px',
          backgroundColor: 'var(--color-brand-primary)',
          color: 'var(--color-text-inverse)',
          borderRadius: '8px',
          textDecoration: 'none',
          fontWeight: 600,
          fontSize: '14px',
        }}
      >
        Sign in now
      </Link>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page component
// ---------------------------------------------------------------------------

export default function ResetPasswordPage() {
  const { token } = useParams<{ token: string }>();

  const [validationState, setValidationState] = useState<
    'loading' | 'invalid' | 'valid'
  >('loading');
  const [tokenPreview, setTokenPreview] = useState<PasswordResetTokenPreview | null>(null);

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [succeeded, setSucceeded] = useState(false);

  // Validate token on mount.
  const didValidate = useRef(false);
  useEffect(() => {
    if (didValidate.current || !token) return;
    didValidate.current = true;

    validatePasswordResetToken(token)
      .then((preview) => {
        setTokenPreview(preview);
        setValidationState(preview.valid ? 'valid' : 'invalid');
      })
      .catch(() => {
        setTokenPreview({ valid: false, error: 'not_found', message: undefined });
        setValidationState('invalid');
      });
  }, [token]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setPasswordError(null);
    setSubmitError(null);

    if (newPassword !== confirmPassword) {
      setPasswordError('Passwords do not match.');
      return;
    }

    if (!token) return;

    setIsSubmitting(true);
    try {
      await resetPassword(token, newPassword);
      setSucceeded(true);
    } catch (err) {
      setSubmitError(getErrorMessage(err, 'Something went wrong. Please try again.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <style>{`
        .mc-submit-btn {
          transition: background-color 150ms ease, box-shadow 150ms ease, transform 100ms ease;
        }
        .mc-submit-btn:not(:disabled):hover {
          background-color: var(--color-brand-primary-hover);
          box-shadow: 0 0 18px rgba(59, 130, 246, 0.30), 0 4px 12px rgba(0, 0, 0, 0.20);
        }
        .mc-submit-btn:not(:disabled):active {
          transform: scale(0.98);
        }
      `}</style>

      {/* Full-screen split layout */}
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          backgroundColor: 'var(--color-surface-base)',
        }}
      >
        {/* ── Left panel (desktop only) ─────────────────────────────────────── */}
        <div
          className="hidden lg:flex"
          style={{
            width: '50%',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            overflow: 'hidden',
            backgroundColor: 'var(--color-surface-base)',
            padding: '64px 56px',
          }}
        >
          <div
            aria-hidden="true"
            style={{
              position: 'absolute',
              top: '-80px',
              right: '-80px',
              width: '560px',
              height: '560px',
              borderRadius: '50%',
              background:
                'radial-gradient(circle, rgba(59,130,246,0.18) 0%, rgba(59,130,246,0.06) 45%, transparent 70%)',
              pointerEvents: 'none',
            }}
          />
          <div
            aria-hidden="true"
            style={{
              position: 'absolute',
              bottom: '-60px',
              left: '-40px',
              width: '360px',
              height: '360px',
              borderRadius: '50%',
              background:
                'radial-gradient(circle, rgba(59,130,246,0.07) 0%, transparent 65%)',
              pointerEvents: 'none',
            }}
          />
          <div
            style={{
              position: 'relative',
              zIndex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textAlign: 'center',
              gap: '28px',
              maxWidth: '380px',
            }}
          >
            <img
              src="/pwa-512.png"
              alt="PointeTracer logo"
              style={{
                width: '280px',
                height: '280px',
                objectFit: 'contain',
                filter:
                  'drop-shadow(0 0 24px rgba(59,130,246,0.45)) drop-shadow(0 8px 24px rgba(0,0,0,0.35))',
              }}
            />
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <h1
                className="text-display-lg"
                style={{
                  color: 'var(--color-text-primary)',
                  margin: 0,
                  letterSpacing: '-0.5px',
                }}
              >
                Choose a new password
              </h1>
              <p
                className="text-body-md"
                style={{ color: 'var(--color-text-secondary)', margin: 0 }}
              >
                Make it strong — at least 12 characters with a letter and a digit.
              </p>
            </div>
          </div>
        </div>

        {/* ── Right panel (content) ─────────────────────────────────────────── */}
        <div
          style={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '32px 24px',
            backgroundColor: 'var(--color-surface-raised-2)',
          }}
          className="lg:w-1/2"
        >
          <div className="w-full" style={{ maxWidth: '400px' }}>
            {/* Mobile-only logo */}
            <div
              className="flex lg:hidden"
              style={{ justifyContent: 'center', marginBottom: '32px' }}
            >
              <img
                src="/pwa-512.png"
                alt="PointeTracer logo"
                style={{
                  width: '64px',
                  height: '64px',
                  objectFit: 'contain',
                  borderRadius: '16px',
                  boxShadow: '0 0 24px rgba(59,130,246,0.25)',
                }}
              />
            </div>

            {/* ── State machine ─────────────────────────────────────────────── */}

            {validationState === 'loading' && (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '16px',
                  paddingTop: '40px',
                }}
              >
                <div
                  style={{
                    width: '32px',
                    height: '32px',
                    border: '3px solid var(--color-border-subtle)',
                    borderTopColor: 'var(--color-brand-primary)',
                    borderRadius: '50%',
                    animation: 'spin 0.7s linear infinite',
                  }}
                />
                <p className="text-body-sm" style={{ color: 'var(--color-text-muted)' }}>
                  Validating link…
                </p>
              </div>
            )}

            {validationState === 'invalid' && tokenPreview && (
              <InvalidTokenScreen
                errorCode={tokenPreview.error}
                message={tokenPreview.message}
              />
            )}

            {validationState === 'valid' && succeeded && <SuccessScreen />}

            {validationState === 'valid' && !succeeded && (
              <>
                <div style={{ marginBottom: '40px' }}>
                  <h2
                    className="text-display-lg"
                    style={{
                      color: 'var(--color-text-primary)',
                      margin: '0 0 8px',
                      letterSpacing: '-0.5px',
                    }}
                  >
                    Set new password
                  </h2>
                  {tokenPreview?.email && (
                    <p
                      className="text-body-sm"
                      style={{ color: 'var(--color-text-muted)', margin: 0 }}
                    >
                      For {tokenPreview.email}
                    </p>
                  )}
                </div>

                <form
                  onSubmit={handleSubmit}
                  noValidate
                  style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}
                >
                  {/* Submit error banner */}
                  {submitError && (
                    <div
                      role="alert"
                      style={{
                        borderRadius: '10px',
                        border: '1px solid rgba(248, 113, 113, 0.30)',
                        backgroundColor: 'rgba(248, 113, 113, 0.08)',
                        padding: '12px 14px',
                        fontSize: '13px',
                        color: 'var(--color-signal-bad)',
                        lineHeight: 1.5,
                      }}
                    >
                      {submitError}
                    </div>
                  )}

                  {/* New password */}
                  <div>
                    <label
                      htmlFor="reset-new-password"
                      className="text-label"
                      style={{
                        display: 'block',
                        color: 'var(--color-text-muted)',
                        marginBottom: '6px',
                      }}
                    >
                      New password
                    </label>
                    <div style={{ position: 'relative' }}>
                      <LockClosedIcon
                        style={{
                          position: 'absolute',
                          left: '12px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          width: '16px',
                          height: '16px',
                          color: 'var(--color-text-muted)',
                          pointerEvents: 'none',
                        }}
                      />
                      <input
                        type={showNewPassword ? 'text' : 'password'}
                        id="reset-new-password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        placeholder="Min. 12 characters"
                        required
                        autoComplete="new-password"
                        disabled={isSubmitting}
                        style={{
                          width: '100%',
                          paddingLeft: '36px',
                          paddingRight: '40px',
                          paddingTop: '10px',
                          paddingBottom: '10px',
                          backgroundColor: 'var(--color-surface-raised-3)',
                          border: `1px solid ${passwordError ? 'rgba(248,113,113,0.6)' : 'var(--color-border-subtle)'}`,
                          borderRadius: '8px',
                          color: 'var(--color-text-primary)',
                          fontSize: '14px',
                          outline: 'none',
                          boxSizing: 'border-box',
                        }}
                      />
                      <PasswordToggleButton
                        visible={showNewPassword}
                        onToggle={() => setShowNewPassword((v) => !v)}
                        disabled={isSubmitting}
                        controls="reset-new-password"
                      />
                    </div>
                    <p
                      className="text-body-xs"
                      style={{ color: 'var(--color-text-muted)', marginTop: '4px', marginBottom: 0 }}
                    >
                      At least 12 characters, one letter, one digit.
                    </p>
                  </div>

                  {/* Confirm password */}
                  <div>
                    <label
                      htmlFor="reset-confirm-password"
                      className="text-label"
                      style={{
                        display: 'block',
                        color: 'var(--color-text-muted)',
                        marginBottom: '6px',
                      }}
                    >
                      Confirm new password
                    </label>
                    <div style={{ position: 'relative' }}>
                      <LockClosedIcon
                        style={{
                          position: 'absolute',
                          left: '12px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          width: '16px',
                          height: '16px',
                          color: 'var(--color-text-muted)',
                          pointerEvents: 'none',
                        }}
                      />
                      <input
                        type={showConfirmPassword ? 'text' : 'password'}
                        id="reset-confirm-password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        placeholder="Repeat your password"
                        required
                        autoComplete="new-password"
                        disabled={isSubmitting}
                        style={{
                          width: '100%',
                          paddingLeft: '36px',
                          paddingRight: '40px',
                          paddingTop: '10px',
                          paddingBottom: '10px',
                          backgroundColor: 'var(--color-surface-raised-3)',
                          border: `1px solid ${passwordError ? 'rgba(248,113,113,0.6)' : 'var(--color-border-subtle)'}`,
                          borderRadius: '8px',
                          color: 'var(--color-text-primary)',
                          fontSize: '14px',
                          outline: 'none',
                          boxSizing: 'border-box',
                        }}
                      />
                      <PasswordToggleButton
                        visible={showConfirmPassword}
                        onToggle={() => setShowConfirmPassword((v) => !v)}
                        disabled={isSubmitting}
                        controls="reset-confirm-password"
                      />
                    </div>
                    {/* Inline password-match error */}
                    {passwordError && (
                      <p
                        role="alert"
                        style={{
                          color: 'var(--color-signal-bad)',
                          fontSize: '12px',
                          marginTop: '4px',
                          marginBottom: 0,
                        }}
                      >
                        {passwordError}
                      </p>
                    )}
                  </div>

                  {/* Submit */}
                  <Button
                    type="submit"
                    disabled={isSubmitting || !newPassword || !confirmPassword}
                    className="mc-submit-btn"
                    style={{
                      width: '100%',
                      padding: '12px',
                      backgroundColor: 'var(--color-brand-primary)',
                      color: 'var(--color-text-inverse)',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '15px',
                      fontWeight: 600,
                      cursor: isSubmitting ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                    }}
                  >
                    {isSubmitting ? (
                      <>
                        <span
                          style={{
                            display: 'inline-block',
                            width: '14px',
                            height: '14px',
                            border: '2px solid rgba(255,255,255,0.3)',
                            borderTopColor: '#fff',
                            borderRadius: '50%',
                            animation: 'spin 0.7s linear infinite',
                          }}
                        />
                        Updating password…
                      </>
                    ) : (
                      'Reset password'
                    )}
                  </Button>

                  {/* Back link */}
                  <div style={{ textAlign: 'center' }}>
                    <Link
                      to="/login"
                      style={{
                        color: 'var(--color-text-muted)',
                        textDecoration: 'none',
                        fontSize: '13px',
                      }}
                    >
                      Back to sign in
                    </Link>
                  </div>
                </form>
              </>
            )}
          </div>
        </div>
      </div>

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </>
  );
}
